import { createFileRoute } from '@tanstack/react-router'

import { WizardStep } from '@/components/onboarding'
import { ChoiceChips, SectionLabel, VenueHero } from '@/components/shared'
import { CATEGORIES, PICKED, TAGS, VENUE_EMPTY, VENUE_FILLED } from '@/mocks/onboarding/podnik'

/** The filled-in state is the reward, so it has to be reviewable on its own. */
type PodnikSearch = { stav?: 'vyplneno' }

export const Route = createFileRoute('/onboarding/podnik')({
  validateSearch: (search: Record<string, unknown>): PodnikSearch =>
    search.stav === 'vyplneno' ? { stav: 'vyplneno' } : {},
  head: () => ({ meta: [{ title: 'Jak podnik vypadá · Aplikace Bitez' }] }),
  component: OnboardingPodnik,
})

/**
 * Step 1 — and deliberately the easiest one: it is answered from memory,
 * nothing has to be found on a computer, and the moment it is done the app
 * looks like theirs. That reward is the only thing onboarding has to give, so
 * it is spent first.
 *
 * The **photo is an invitation inside the preview, not a field**: whoever has
 * one in their phone taps it here, whoever does not walks on — the first shoot
 * produces venue photos in week one anyway, and stalling somebody on the very
 * first screen over a picture is the worst possible place to stall them.
 *
 * The screen is built around the **live preview**: the card at the top is the
 * very `VenueHero` their profile will render, filling in as they tap. Two
 * upload fields and a dropdown would collect the same data and mean nothing.
 */
function OnboardingPodnik() {
  const { stav } = Route.useSearch()
  const filled = stav === 'vyplneno'

  return (
    <WizardStep
      step={1}
      backHref="/onboarding/vitejte"
      nextHref="/onboarding/hlas"
      note="Takhle vás uvidíme my i vy. Fotku i zaměření jde kdykoliv vyměnit."
    >
      <VenueHero venue={filled ? VENUE_FILLED : VENUE_EMPTY} tagsEditable={false} />

      <section className="mt-9">
        <SectionLabel>Typ podniku</SectionLabel>
        <div className="mt-3">
          <ChoiceChips choices={CATEGORIES} selected={filled ? [PICKED.category] : []} />
        </div>
      </section>

      <section className="mt-8">
        <SectionLabel>Čím se lišíte</SectionLabel>
        <p className="mt-1 text-[10px] font-semibold text-ink/40">Vyberte až tři.</p>
        <div className="mt-3">
          <ChoiceChips choices={TAGS} selected={filled ? PICKED.tags : []} />
        </div>
      </section>
    </WizardStep>
  )
}
